import { cn } from "./classNames-DcGnTh2N.js";
import { Search } from "./icons-ZIeZXXS7.js";
import { ScrollArea } from "./internal/components/ScrollArea.js";
import { BrandLogo } from "./BrandLogo-DkESE19K.js";
import { Root, Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
import * as RadixAvatar from "@radix-ui/react-avatar";
import BoringAvatar from "boring-avatars";
//#region src/components/Navbar/NavbarContent.tsx
const NavbarContent = ({ children, className }) => /* @__PURE__ */ jsx(ScrollArea, {
	type: "auto",
	orientation: "horizontal",
	className: "tw:flex-1",
	scrollBarClassName: "tw:h-1.5",
	children: /* @__PURE__ */ jsx("div", {
		className: cn("tw:flex", "tw:py-1.5", className),
		children
	})
});
//#endregion
//#region src/components/Navbar/NavbarFooter.tsx
const NavbarFooter = ({ children, className, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	className: cn("tw:flex tw:items-center tw:gap-2 tw:py-1.5", className),
	children
});
//#endregion
//#region src/components/Navbar/NavbarHeader.tsx
const NavbarHeader = ({ children, className, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	className: cn("tw:overflow-hidden", "tw:py-1.5", className),
	children: children ?? /* @__PURE__ */ jsx(BrandLogo, {
		variant: "wordmark",
		className: "tw:h-7"
	})
});
//#endregion
//#region src/components/Navbar/NavbarMenu.tsx
const variants$2 = cva([
	"tw:m-0",
	"tw:p-0",
	"tw:flex",
	"tw:items-center",
	"tw:justify-center",
	"tw:gap-2"
], {
	variants: { marker: {
		none: [],
		"forward-slash": [
			"tw:[&_li]:flex",
			"tw:[&_li]:items-center",
			"tw:[&_li]:before:content-[\"/\"]",
			"tw:[&_li]:before:text-border",
			"tw:[&_li]:before:text-sm",
			"tw:[&_li]:before:mr-0.5",
			"tw:[&_li:not(:first-child)]:before:ml-0.5"
		]
	} },
	defaultVariants: { marker: "none" }
});
const NavbarMenu = ({ className, marker, ...rest }) => /* @__PURE__ */ jsx("ul", {
	...rest,
	className: variants$2({
		marker,
		className
	})
});
//#endregion
//#region src/components/Navbar/NavbarMenuAvatar.tsx
const NavbarMenuAvatar = ({ ref, className, type = "button", src, alt, ...rest }) => {
	const altText = alt ?? "Avatar";
	return /* @__PURE__ */ jsx("button", {
		...rest,
		ref,
		type,
		className: cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:size-8", "tw:aspect-square", "tw:overflow-hidden", "tw:shrink-0", "tw:rounded-lg", "tw:bg-transparent", "tw:cursor-pointer", "tw:disabled:opacity-disabled", "tw:disabled:cursor-not-allowed", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-sidebar-ring/50", className),
		children: /* @__PURE__ */ jsxs(RadixAvatar.Root, { children: [/* @__PURE__ */ jsx(RadixAvatar.Image, {
			src,
			alt: altText,
			className: "tw:size-full tw:object-cover"
		}), /* @__PURE__ */ jsx(RadixAvatar.Fallback, {
			asChild: true,
			children: /* @__PURE__ */ jsx(BoringAvatar, {
				square: true,
				name: altText,
				variant: "beam",
				colors: [
					"#1F8FFF",
					"#004787",
					"#28D8FF",
					"#925cff",
					"#A8C2E6"
				],
				size: 32
			})
		})] })
	});
};
//#endregion
//#region src/components/Navbar/NavbarMenuItemAdornment.tsx
const variants$1 = cva([
	"tw:flex",
	"tw:justify-center",
	"tw:items-center",
	"tw:[&_svg:not([class*=\"tw:size-\"])]:size-3",
	"tw:[&:not(svg)]:h-5",
	"tw:[&:not(svg)]:min-w-5",
	"tw:p-1",
	"tw:aspect-square",
	"tw:rounded-md"
], {
	defaultVariants: { variant: "icon-accent" },
	variants: { variant: {
		icon: ["tw:text-sidebar-foreground", "tw:[&_svg]:text-sidebar-foreground"],
		"icon-emphasized": ["tw:text-white", "tw:[&_svg]:text-white"],
		"icon-accent": [
			"tw:group-data-[accent=blue]/navbar:text-sidebar-primary",
			"tw:group-data-[accent=blue]/navbar:[&_svg]:text-sidebar-primary",
			"tw:group-data-[accent=purple]/navbar:text-purple-500",
			"tw:group-data-[accent=purple]/navbar:[&_svg]:text-purple-500",
			"tw:group-data-[accent=orange]/navbar:text-warning-500",
			"tw:group-data-[accent=orange]/navbar:[&_svg]:text-warning-500",
			"tw:group-data-[accent=green]/navbar:text-primary-500",
			"tw:group-data-[accent=green]/navbar:[&_svg]:text-primary-500"
		],
		"icon-accent-emphasized": [
			"tw:text-white",
			"tw:[&_svg]:text-white",
			"tw:group-data-[accent=blue]/navbar:bg-sidebar-primary",
			"tw:group-data-[accent=purple]/navbar:bg-purple-500",
			"tw:group-data-[accent=orange]/navbar:bg-warning-500",
			"tw:group-data-[accent=green]/navbar:bg-primary-500"
		],
		"icon-button": [
			"tw:size-4",
			"tw:!p-0",
			"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
		],
		shortcut: [
			"tw:bg-muted",
			"tw:text-sidebar-foreground",
			"tw:ml-auto"
		]
	} }
});
const NavbarMenuItemAdornment = ({ ref, asChild = false, className, variant = "icon-accent", ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "span", {
		...props,
		ref,
		className: variants$1({
			variant,
			className
		})
	});
};
//#endregion
//#region src/components/Navbar/NavbarMenuButton.tsx
const variants = cva([
	"tw:group/navbar-menu-button",
	"tw:box-border",
	"tw:flex",
	"tw:items-center",
	"tw:gap-2",
	"tw:bg-transparent",
	"tw:leading-9",
	"tw:border",
	"tw:rounded-lg",
	"tw:whitespace-nowrap",
	"tw:select-none",
	"tw:!outline-hidden",
	"tw:focus-visible:ring-2",
	"tw:focus-visible:ring-sidebar-ring/50",
	"tw:disabled:opacity-disabled",
	"tw:disabled:hover:bg-transparent",
	"tw:disabled:cursor-not-allowed"
], {
	defaultVariants: { variant: "default" },
	variants: { variant: {
		default: [
			"tw:max-h-9",
			"tw:border-transparent",
			"tw:py-2",
			"tw:px-1.5",
			"tw:text-sidebar-foreground",
			"tw:hover:bg-sidebar-hover",
			"tw:aria-expanded:bg-sidebar-hover",
			"tw:aria-[current=page]:bg-sidebar-selected",
			"tw:aria-[current=page]:hover:bg-sidebar-selected",
			"tw:aria-[current=page]:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]",
			"tw:aria-[current=page]:font-medium"
		],
		emphasized: [
			"tw:max-h-9",
			"tw:border-transparent",
			"tw:py-2",
			"tw:px-1.5",
			"tw:text-sidebar-foreground",
			"tw:hover:bg-sidebar-hover",
			"tw:aria-expanded:bg-sidebar-hover",
			"tw:aria-[current=page]:bg-sidebar-selected",
			"tw:aria-[current=page]:hover:bg-sidebar-selected",
			"tw:aria-[current=page]:text-[var(--sidebar-selected-foreground,rgb(var(--sidebar-foreground)))]",
			"tw:font-semibold"
		],
		icon: [
			"tw:size-8",
			"tw:aspect-square",
			"tw:justify-center",
			"tw:font-normal",
			"tw:border-transparent",
			"tw:text-primary",
			"tw:[&_svg]:!text-primary",
			"tw:hover:bg-sidebar-hover",
			"tw:aria-expanded:bg-sidebar-hover"
		],
		search: [
			"tw:max-h-8",
			"tw:min-w-52",
			"tw:px-3",
			"tw:py-1",
			"tw:font-normal",
			"tw:border-input",
			"tw:text-muted-foreground",
			"tw:[&_svg]:!text-muted-foreground"
		]
	} }
});
const NavbarMenuButton = ({ ref, className, type = "button", children, icon, asChild = false, variant = "default", ...rest }) => {
	const Comp = asChild ? Root : "button";
	const isSearchVariant = variant === "search";
	const isEmphasizedVariant = variant === "emphasized";
	const adornment = isSearchVariant ? /* @__PURE__ */ jsx(Search, {}) : icon;
	const adornmentVariant = (() => {
		if (isSearchVariant || variant === "icon") return "icon-button";
		if (isEmphasizedVariant) return "icon-accent-emphasized";
		return "icon-accent";
	})();
	return /* @__PURE__ */ jsxs(Comp, {
		...rest,
		ref,
		type,
		"data-variant": variant,
		className: cn(variants({
			className,
			variant
		})),
		children: [adornment && /* @__PURE__ */ jsx(NavbarMenuItemAdornment, {
			variant: adornmentVariant,
			children: adornment
		}), /* @__PURE__ */ jsx(Slottable, { children })]
	});
};
//#endregion
//#region src/components/Navbar/NavbarMenuItem.tsx
const NavbarMenuItem = ({ className, ...rest }) => /* @__PURE__ */ jsx("li", {
	...rest,
	className: cn("tw:flex", "tw:items-center", "tw:gap-2", className)
});
//#endregion
//#region src/components/Navbar/Navbar.tsx
const navbarClassnames = cva([
	"tailwindcss-preflight",
	"tw:group/navbar",
	"tw:sticky",
	"tw:top-0",
	"tw:z-50",
	"tw:flex",
	"tw:gap-2",
	"tw:items-center",
	"tw:pl-2",
	"tw:pr-3",
	"tw:bg-sidebar",
	"tw:text-sidebar-foreground",
	"tw:text-xs",
	"tw:leading-4"
], {
	defaultVariants: { bordered: false },
	variants: { bordered: {
		true: [
			"tw:before:content",
			"tw:before:h-0.5",
			"tw:before:w-full",
			"tw:before:absolute",
			"tw:before:left-0",
			"tw:before:top-0",
			"tw:data-[accent=blue]:before:bg-sidebar-primary",
			"tw:data-[accent=purple]:before:bg-purple-500",
			"tw:data-[accent=green]:before:bg-primary-500",
			"tw:data-[accent=orange]:before:bg-warning-500"
		],
		false: ""
	} }
});
/**
* Top navigation bar for the app shell, hosted inside `<AppShell.Header>`.
*
* - Composition: `Navbar.Header` (brand), `Navbar.Content` (scrollable menu), `Navbar.Footer`.
* - `bordered` adds a top hairline coloured by `accent`.
* - Override the default `aria-label` when a page has more than one nav.
*
* @see AppShell — the sidebar rail uses `Sidebar` instead.
*/
const Navbar = ({ className, accent = "blue", bordered = false, ...rest }) => /* @__PURE__ */ jsx("nav", {
	"aria-label": "Main navigation",
	...rest,
	"data-slot": "navbar",
	"data-accent": accent,
	className: navbarClassnames({
		bordered,
		className
	})
});
Navbar.Header = NavbarHeader;
Navbar.Footer = NavbarFooter;
Navbar.Content = NavbarContent;
Navbar.Menu = NavbarMenu;
Navbar.MenuItem = NavbarMenuItem;
Navbar.MenuButton = NavbarMenuButton;
Navbar.MenuItemAdornment = NavbarMenuItemAdornment;
Navbar.MenuAvatar = NavbarMenuAvatar;
//#endregion
export { Navbar };

//# sourceMappingURL=Navbar-DHUS7XiU.js.map